/** /partners/ — the integration partner registry (dev-platform metaplan G1; GATE D2).
 *
 *  A partner (booking-system vendor, operator in-house team, agency) is the gate of
 *  the partner developer programme: only an active partner with a recorded contract
 *  whose term has not ended may hold a developer key. It works in one stand-alone
 *  sandbox tenant, `<partner code>sbx`, created with it (or later, `createSandbox`).
 *
 *  Shapes come from the generated spec types; every write is audited server-side
 *  (`partner.*`). The contract PDF never travels as a URL: it is uploaded through
 *  the API and downloaded as bytes (`downloadContract`). */
import { apiDelete, apiGet, apiPatch, apiPost, client } from './client';
import type { components } from './types';
import type { PaginatedResponse } from './users';
import type { SuggestionFetcher } from '@/components/QuerySearch';

export type Partner = components['schemas']['ConsolePartnerListItem'];
export type PartnerDetail = components['schemas']['ConsolePartnerDetail'];
export type PartnerContact = components['schemas']['ConsolePartnerContact'];
export type PartnerCreate = components['schemas']['ConsolePartnerCreate'];
export type PartnerUpdate = components['schemas']['ConsolePartnerUpdate'];
export type PartnerContactWrite = components['schemas']['ConsolePartnerContactWrite'];
export type PartnerContactUpdate = components['schemas']['ConsolePartnerContactUpdate'];
/** Every field has a server default (generated types mark defaulted fields required). */
export type PartnerSandboxCreate = Partial<components['schemas']['ConsolePartnerSandboxCreate']>;
export type PartnerCodeCheck = components['schemas']['ConsolePartnerCodeCheck'];
export type PartnerKind = Partner['kind'];
export type PartnerStatus = Partner['status'];
/** The partner's own developer keys (scope `partner_dev`, root keys); managed on
 *  /api-credentials/:id. */
export type PartnerDeveloperKey = components['schemas']['ConsolePartnerDeveloperKey'];
export type PartnerClaimLink = components['schemas']['ConsolePartnerClaimLink'];
export type PartnerClaimLinkCreate = components['schemas']['ConsolePartnerClaimLinkCreate'];
export type PartnerClaimLinkStatus = PartnerClaimLink['status'];
/** `{id, code, name}` — how other resources name a partner. */
export type PartnerRef = components['schemas']['ConsolePartnerRef'];

export const PARTNER_KIND_LABELS: Record<PartnerKind, string> = {
  booking_system_vendor: 'Booking-system vendor',
  operator_in_house: 'Operator in-house team',
  agency: 'Agency',
};

export const PARTNER_STATUS_LABELS: Record<PartnerStatus, string> = {
  active: 'Active',
  suspended: 'Suspended',
  terminated: 'Terminated',
};

/** Why the partner may not hold or use a developer key now (null = it may). */
export const GATE_REFUSAL_LABELS: Record<string, string> = {
  partner_suspended: 'Suspended — keys denied',
  partner_terminated: 'Terminated — keys revoked',
  partner_contract_missing: 'No recorded contract',
  partner_contract_expired: 'Contract term ended',
};

export const CLAIM_LINK_STATUS_LABELS: Record<PartnerClaimLinkStatus, string> = {
  pending: 'Pending',
  claimed: 'Claimed',
  expired: 'Expired',
  revoked: 'Revoked',
};

/** Integrations a partner sandbox can be created with (restech codes). The XML/JSON
 *  push (`curatedplanet`) is the default. */
export const SANDBOX_INTEGRATIONS: { code: string; label: string }[] = [
  { code: 'curatedplanet', label: 'XML / JSON push' },
  { code: 'ventrata', label: 'Ventrata' },
  { code: 'fareharbor', label: 'FareHarbor' },
  { code: 'bokun', label: 'Bókun' },
  { code: 'rezdy', label: 'Rezdy' },
];

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** Comma-separated statuses. */
  status?: string;
  /** Comma-separated kinds. */
  kind?: string;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.status) usp.set('status', params.status);
  if (params.kind) usp.set('kind', params.kind);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<Partner>> {
  return apiGet<PaginatedResponse<Partner>>(`/partners/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<PartnerDetail> {
  return apiGet<PartnerDetail>(`/partners/${id}/`);
}

export function create(body: PartnerCreate): Promise<PartnerDetail> {
  return apiPost<PartnerDetail>('/partners/', body);
}

export function update(id: number, body: PartnerUpdate): Promise<PartnerDetail> {
  return apiPatch<PartnerDetail>(`/partners/${id}/`, body);
}

export function checkCode(code: string): Promise<PartnerCodeCheck> {
  return apiGet<PartnerCodeCheck>(`/partners/check_code/?code=${encodeURIComponent(code)}`);
}

export type LifecycleStep = 'suspend' | 'resume' | 'terminate';

export function lifecycle(id: number, step: LifecycleStep, reason = ''): Promise<PartnerDetail> {
  return apiPost<PartnerDetail>(`/partners/${id}/${step}/`, { reason });
}

export function createSandbox(id: number, body: PartnerSandboxCreate = {}): Promise<PartnerDetail> {
  return apiPost<PartnerDetail>(`/partners/${id}/sandbox/`, body);
}

export function addContact(id: number, body: PartnerContactWrite): Promise<PartnerContact> {
  return apiPost<PartnerContact>(`/partners/${id}/contacts/`, body);
}

export function updateContact(id: number, contactId: number, body: PartnerContactUpdate): Promise<PartnerContact> {
  return apiPatch<PartnerContact>(`/partners/${id}/contacts/${contactId}/`, body);
}

export function removeContact(id: number, contactId: number): Promise<void> {
  return apiDelete(`/partners/${id}/contacts/${contactId}/`);
}

/** A 20 MB PDF on a slow link outlives the client's 30 s default. */
const UPLOAD_TIMEOUT_MS = 5 * 60_000;

/** POST /partners/{id}/contract/ — the signed PDF (the server checks the bytes). */
export async function uploadContract(id: number, file: File): Promise<PartnerDetail> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  const { data } = await client.post<PartnerDetail>(`/partners/${id}/contract/`, fd, {
    // Let axios set the multipart boundary itself.
    headers: { 'Content-Type': undefined },
    timeout: UPLOAD_TIMEOUT_MS,
  });
  return data;
}

/** GET /partners/{id}/contract/ — the PDF bytes (an attachment; never a URL to the object). */
export async function downloadContract(id: number): Promise<Blob> {
  const { data } = await client.get<Blob>(`/partners/${id}/contract/`, {
    responseType: 'blob',
    timeout: UPLOAD_TIMEOUT_MS,
  });
  return data;
}

// ---- Claim links (metaplan G2; GATE D7) ----------------------------------------
// A one-time, 72-hour link e-mailed to a technical contact: opening it shows a preview,
// confirming on the page mints the partner's developer key and shows it ONCE, to the
// contact. Ops never see the key — nothing here carries it.

/** GET /partners/{id}/claim_links/ — newest first. */
export function listClaimLinks(id: number): Promise<PartnerClaimLink[]> {
  return apiGet<PartnerClaimLink[]>(`/partners/${id}/claim_links/`);
}

/** POST — sends a new link (earlier pending links are revoked). Also how a key is rotated.
 *  409 `{detail, code}` when the partner cannot receive a key now; 503 `claim_mail_failed`. */
export function sendClaimLink(id: number, body: PartnerClaimLinkCreate = {}): Promise<PartnerClaimLink> {
  return apiPost<PartnerClaimLink>(`/partners/${id}/claim_links/`, body);
}

/** POST …/revoke/ — 409 when the link is no longer pending. */
export function revokeClaimLink(id: number, linkId: number): Promise<PartnerClaimLink> {
  return apiPost<PartnerClaimLink>(`/partners/${id}/claim_links/${linkId}/revoke/`);
}

/** QuerySearch suggestions for a `partner` namespace (External access, API credentials):
 *  the value is the partner code, `id` powers the `=#` match-by-id commit. Needs
 *  `console_partners_view`; without it the list 403s and the widget just offers nothing. */
export function makePartnerSuggestionFetcher(): SuggestionFetcher {
  return async (query, _extra, signal) => {
    const usp = new URLSearchParams({ page_size: '15', ordering: 'name' });
    if (query) usp.set('search', query);
    const res = await apiGet<PaginatedResponse<Partner>>(`/partners/?${usp.toString()}`, { signal });
    return res.results.map((p) => ({ value: p.code, label: p.code, description: p.name, id: p.id }));
  };
}
